import type { CorrectionMove, InboundCorrectionView, InboundShipmentLine, ReceiptPlace } from '@wms/domain';

/**
 * The words of a mal kabul correction (Düzelt): what a change will do before it is pressed, and
 * what one did, for the warehouse and for the seller.
 */

/** "3 sağlam" / "2 sağlam, 1 hasarlı" */
export const countsText = (c: { readonly received: number; readonly damaged: number }) => `${c.received} sağlam${c.damaged > 0 ? `, ${c.damaged} hasarlı` : ''}`;

/** "kabul alanında (KB-01-1-1) 2 · A-01-2-1 gözünde 1 · hasarlı alanında 1" */
export function placesText(places: readonly ReceiptPlace[]): string {
  if (places.length === 0) return 'stokta kalmadı';
  return places
    .map((p) =>
      p.kind === 'dock' ? `kabul alanında (${p.locationCode}) ${p.qty}` : p.kind === 'damaged' ? `hasarlı alanında (${p.locationCode}) ${p.qty}` : `${p.locationCode} gözünde ${p.qty}`,
    )
    .join(' · ');
}

/** "KB-01-1-1'den 1 adet çıkarıldı · KB-01-1-1'e 2 adet eklendi" */
export function movesText(moves: readonly CorrectionMove[]): string {
  return moves.map((m) => (m.from ? `${m.from}: ${m.qty} adet çıkarıldı` : `${m.to ?? 'kabul alanı'}: ${m.qty} adet eklendi`)).join(' · ');
}

/** Against what the seller announced: "1 eksik", "2 fazla", "tam". */
export function againstExpected(expected: number, counted: number): string {
  const diff = counted - expected;
  if (expected === 0) return counted > 0 ? `gönderide yoktu, ${counted} fazla` : 'gönderide yoktu';
  return diff === 0 ? 'beklenen adet tam' : diff < 0 ? `${-diff} eksik` : `${diff} fazla`;
}

/** One correction in a line: "3 sağlam → 2 sağlam (1 eksik)", or what was marked. */
export function correctionSummary(c: InboundCorrectionView): string {
  if (c.kind === 'mark_short') return `${c.after.received + c.after.damaged}/${c.expectedQty} geldi, kalanı gelmeyecek`;
  if (c.kind === 'unmark_short') return `${c.after.received + c.after.damaged}/${c.expectedQty} geldi, kalanı yine bekleniyor`;
  return `${countsText(c.before)} → ${countsText(c.after)} (${againstExpected(c.expectedQty, c.after.received + c.after.damaged)})`;
}

export interface FixPreview {
  /** what the correction does, in order */
  readonly steps: readonly string[];
  /** units it takes out of bins: the depo yöneticisi's call */
  readonly fromShelf: number;
  /** the bins put-away units of the line lie in */
  readonly bins: readonly string[];
  /** the line against the announcement afterwards */
  readonly result: string;
  readonly changed: boolean;
}

/**
 * What setting a line's counts will do, from where its units are now: good units leave the dock
 * first, then their bin; damaged ones the damaged area; units added come in at the dock (good, with a
 * putaway task) or the damaged area.
 */
export function fixPreview(
  line: Pick<InboundShipmentLine, 'expectedQty' | 'receivedQty' | 'damagedQty'>,
  places: readonly ReceiptPlace[],
  next: { readonly good: number; readonly damaged: number },
): FixPreview {
  const steps: string[] = [];
  const atDock = places.filter((p) => p.kind === 'dock').reduce((n, p) => n + p.qty, 0);
  const bins = [...new Set(places.filter((p) => p.kind === 'bin').map((p) => p.locationCode))];
  let fromShelf = 0;
  const goodDown = line.receivedQty - next.good;
  if (goodDown > 0) {
    const fromDock = Math.min(goodDown, atDock);
    fromShelf = goodDown - fromDock;
    if (fromDock > 0) steps.push(`${fromDock} adet kabul alanından çıkarılır; yerleştirme görevi küçülür`);
    if (fromShelf > 0) steps.push(`${fromShelf} adet rafa yerleştirilmiş üründen çıkarılır (${bins.join(', ') || 'göz'}); siparişe ayrılan ürüne dokunulmaz`);
  } else if (goodDown < 0) {
    steps.push(`${-goodDown} sağlam adet eklenir: kabul alanına girer, yerleştirme görevi açılır`);
  }
  const damagedDown = line.damagedQty - next.damaged;
  if (damagedDown > 0) steps.push(`${damagedDown} adet hasarlı alanından çıkarılır`);
  else if (damagedDown < 0) steps.push(`${-damagedDown} hasarlı adet eklenir (hasarlı alanına, satışa açılmaz)`);
  return {
    steps,
    fromShelf,
    bins,
    result: againstExpected(line.expectedQty, next.good + next.damaged),
    changed: goodDown !== 0 || damagedDown !== 0,
  };
}
